import { useAuthContext } from "../../auth/state/AuthContext";
import { themeAtom } from "../../shared/atoms";
import { ErrorDisplay } from "../../shared/components/ErrorDisplay";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { toastError } from "../../shared/components/ToastContainer";
import { log, logE, logV } from "../../shared/services/debug-logger";
import { api, apiUrl } from "../../shared/services/service";
import type { StepNineFinalProps } from "../models/tech-form-validation-ui.models";
import { TECH_FORM_CACHE_MODES } from "../models/tech-form-vessel-cache.constants";
import { invalidateTechFormVesselDataCache } from "../services/tech-form-section-data.service";
import { clearSubmittedTechFormVesselDraftAtom } from "../state/tech-form-vessel-cache.atoms";
import { datadogRum } from "@datadog/browser-rum";
import { useAtomValue, useSetAtom } from "jotai";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { twMerge } from "tailwind-merge";

export const Tq9Confirmation = ({
  isCompletedForm,
  goToLandingPage,
  goToPrev,
  onSubmissionSucceeded,
  validateBeforeSubmit,
}: StepNineFinalProps) => {
  const clearSubmittedVesselDraft = useSetAtom(clearSubmittedTechFormVesselDraftAtom);
  const theme = useAtomValue(themeAtom);

  const { sub: email } = useAuthContext().state.user?.accessToken || {};
  const { vesselName, imoNumber } = useParams();
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const completionTimeout = useRef<number | null>(null);
  const isMounted = useRef(true);
  logV("Tq9Confirmation - BUILD", { loading: isLoading, submitted });

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (completionTimeout.current !== null) window.clearTimeout(completionTimeout.current);
    };
  }, []);

  const submitTq = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isLoading || submitted) return;
    log("Tq9Confirmation - submitTq");
    setIsLoading(true);
    try {
      if (await validateBeforeSubmit()) await submitTechForm();
      else log("Tq9Confirmation - submitTq - validation blocked");
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  };

  const handleFail = (error: any) => {
    toastError(`Unable to submit the technical questionnaire. ${error?.message || "Please retry."}`);
  };

  const submitTechForm = async () => {
    try {
      const requestData = {
        ext_user_email: email,
        vessel_name: vesselName,
        imo_number: imoNumber,
      };
      const response = await api.post(`${apiUrl}/extuserTechFormSubmission`, requestData, { params: requestData });
      log("REPO - submitTechForm - response", { status: response.status });
      if (isMounted.current) onSubmissionSucceeded();
      const submittedVessel = {
        imoNumber: imoNumber ?? "",
        vesselName: vesselName ?? "",
        mode: isCompletedForm ? TECH_FORM_CACHE_MODES.completed : TECH_FORM_CACHE_MODES.editable,
      };
      clearSubmittedVesselDraft(submittedVessel);
      invalidateTechFormVesselDataCache(submittedVessel);
      if (!isCompletedForm) {
        datadogRum.addAction("techFormSubmitted", {
          ext_user_email: email,
          vessel_name: vesselName,
          imo_number: imoNumber,
        });
      }
      if (!isMounted.current) {
        return;
      }
      setSubmitted(true);
      goToLandingPageOnCompletion();
    } catch (error: unknown) {
      logE(new Error("Tq9Confirmation - submitTechForm - failed"));
      if (isMounted.current) handleFail(error);
    }
  };

  const goToLandingPageOnCompletion = () => {
    completionTimeout.current = window.setTimeout(() => {
      completionTimeout.current = null;
      if (!isMounted.current) return;
      log("Tq9Confirmation - goToLandingPageOnCompletion");
      goToLandingPage();
    }, 3000);
  };

  const today = new Date();
  const formattedDate = today.toDateString();
  const nameArray = vesselName?.split(" ");
  const firstName = nameArray?.[0]?.charAt(0) || "";
  const lastNameFirstLetter = (nameArray?.length ?? 0) > 1 ? nameArray?.[1]?.charAt(0) : "";

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorClassName="text-green" loadingIndicatorSize="lg">
      <div
        className={twMerge(
          "p-2 md:p-4",
          theme.home.tailwind.panelBackground,
          theme.home.tailwind.textDefault,
          theme.balticQuestionnaire.labelsText,
        )}
      >
        <h1 className="custom-heading pb-4">Confirm submission</h1>
        <ToastContainer />
        <div className="m-0 w-3/5 p-1 text-sm">
          <ErrorDisplay category="TQ" />
        </div>
        <div className="pb-4">
          <label>You’re about to send the technical form to Cargill. Please, confirm your submission.</label>
        </div>

        <form onSubmit={submitTq} className="w-3/5">
          <div className={twMerge("grid grid-cols-5 gap-4 rounded-lg p-2", theme.balticQuestionnaire.lastScreenBg)}>
            <div className="mb-4">
              <div className="block pb-2 text-sm">Submitted by</div>
              <div className="flex justify-start">
                <p
                  className={twMerge(
                    "rounded-full px-1 text-sm",
                    theme.sideBarComponent.tailWind.white,
                    theme.headerComponent.logInCircle.text,
                  )}
                >
                  {firstName}
                  {lastNameFirstLetter}
                </p>
                <p className="pl-2 text-sm">{vesselName}</p>
              </div>
            </div>
            <div className="mb-4">
              <div className="block pb-2 text-sm">Date</div>
              <div className="text-sm">{formattedDate}</div>
            </div>
            <div className="mb-4">
              <div className="block pb-2 text-sm">Vessel name</div>
              <div className="text-sm">{vesselName}</div>
            </div>
            <div className="mb-4">
              <div className="block pb-2 text-sm">IMO Number</div>
              <div className="flex items-center text-sm">{imoNumber}</div>
            </div>
          </div>

          {/* Prev or Submit */}
          <div className="flex justify-start pt-4">
            <button
              className={twMerge(
                "m-2 rounded px-4 py-2 focus:outline-none",
                theme.myVesselsTableRow.row.primary,
                theme.headerComponent.tailwind.buttonText,
              )}
              type="button"
              disabled={isLoading || submitted}
              onClick={goToPrev}
            >
              Previous
            </button>
            <button
              type="submit"
              disabled={isLoading || submitted}
              className={twMerge(
                "m-2 rounded px-4 py-2 focus:outline-none",
                isLoading || submitted
                  ? twMerge(
                      "cursor-not-allowed text-gray-500",
                      theme.balticQuestionnaire.dividerBg,
                      theme.myVesselsTableRow.tableText.secondary,
                    )
                  : twMerge(theme.uwhcAssistant.sendBg, theme.uwhcAssistant.sendText),
              )}
            >
              Submit
            </button>
          </div>
        </form>
        <div className="grid grid-cols-2 gap-2" />
      </div>
    </LoadingWrapper>
  );
};
